import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../context/AppContext';
import {
    FileText, Calendar, Filter, Download,
    AlertCircle, DollarSign, TrendingDown, TrendingUp,
    Building2, ChevronDown, Plus, Search, Printer,
    Layers, CheckCircle2, Eye, EyeOff
} from 'lucide-react';
import * as XLSX from 'xlsx';
import Modal from '../components/Modal';

const fmt = (v) => new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);

export default function ChartOfAccounts() {
    const { t, i18n } = useTranslation();
    const { authFetch, user, systemSettings } = useContext(AppContext);
    const isAdmin = user?.role === 'Admin' || user?.role === 'Manager';
    const isRtl = i18n.language === 'ar';

    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const today = now.toISOString().split('T')[0];

    const [startDate, setStartDate] = useState(firstDay);
    const [endDate, setEndDate] = useState(today);

    // Active Navigation Tab: 'summary' | 'accounts'
    const [activeTab, setActiveTab] = useState('summary');

    // ── Summary Data State ──
    const [summary, setSummary] = useState({ depos: [], totals: {} });
    const [summaryLoading, setSummaryLoading] = useState(false);

    // ── Accounts Data State ──
    const [accounts, setAccounts] = useState([]);
    const [accountsLoading, setAccountsLoading] = useState(false);
    const [searchQ, setSearchQ] = useState('');
    const [typeFilter, setTypeFilter] = useState('All');

    // ── Minimizing & Collapsing States ──
    const [hideInactiveBranches, setHideInactiveBranches] = useState(true);
    const [branchSearch, setBranchSearch] = useState('');
    const [collapsedBranches, setCollapsedBranches] = useState({});
    const [collapsedCategories, setCollapsedCategories] = useState({});
    const [branchViewMode, setBranchViewMode] = useState('cards'); // 'cards' | 'table'

    // ── Account CRUD Modals ──
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [form, setForm] = useState({ code: '', name: '', type: 'Asset' });
    const [editForm, setEditForm] = useState({ id: '', code: '', name: '', type: 'Asset' });

    // ── Fetch Summary ──
    const fetchSummary = async () => {
        setSummaryLoading(true);
        try {
            const res = await authFetch(`/api/accounts?summary=1&start=${startDate}&end=${endDate}`);
            if (res.ok) {
                const data = await res.json();
                setSummary({ depos: data.depos || [], totals: data.totals || {} });
            }
        } catch (e) {
            console.error(e);
        } finally {
            setSummaryLoading(false);
        }
    };

    // ── Fetch Accounts ──
    const fetchAccounts = async () => {
        setAccountsLoading(true);
        try {
            const res = await authFetch(`/api/accounts?start=${startDate}&end=${endDate}`);
            if (res.ok) {
                const data = await res.json();
                setAccounts(Array.isArray(data) ? data : []);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setAccountsLoading(false);
        }
    };

    useEffect(() => {
        fetchSummary();
        fetchAccounts();
    }, [startDate, endDate]);

    // ── Financial Calculations ──
    const cashIn = parseFloat(summary.totals?.cash_in || 0);
    const totalSalesBilled = parseFloat(summary.totals?.total_sales || 0);
    const totalPurchases = parseFloat(summary.totals?.total_purchases || 0);
    const totalExpenses = parseFloat(summary.totals?.total_expenses || 0);

    // Operating Cash Flow (Net Cash Inflow - Outflows in period)
    const netCashFlow = cashIn - totalPurchases - totalExpenses;

    // Inventory & Assets
    const totalInventoryRetail = summary.depos.reduce((sum, d) => sum + (parseFloat(d.inventory_retail) || 0), 0);
    const totalInventoryCost = summary.depos.reduce((sum, d) => sum + (parseFloat(d.inventory_cost) || 0), 0);
    const unrealizedInventoryMargin = totalInventoryRetail - totalInventoryCost;
    const totalConsolidatedCapital = netCashFlow + totalInventoryRetail;

    // ── Process Depos ──
    const processedDepos = useMemo(() => {
        return (summary.depos || []).map(d => {
            const cIn = parseFloat(d.cash_in || 0);
            const purch = parseFloat(d.total_purchases || 0);
            const exp = parseFloat(d.total_expenses || 0);
            const stockCost = parseFloat(d.inventory_cost || 0);
            const stockRetail = parseFloat(d.inventory_retail || 0);
            const invCount = parseInt(d.invoice_count || 0, 10);
            const poCount = parseInt(d.purchase_count || 0, 10);
            const expCount = parseInt(d.expense_count || 0, 10);
            const net = cIn - purch - exp;

            const hasActivity = cIn > 0 || purch > 0 || exp > 0 || stockCost > 0 || stockRetail > 0 || invCount > 0 || poCount > 0 || expCount > 0;

            return {
                ...d,
                cIn,
                purch,
                exp,
                stockCost,
                stockRetail,
                invCount,
                poCount,
                expCount,
                net,
                hasActivity,
            };
        });
    }, [summary.depos]);

    const filteredDepos = useMemo(() => {
        return processedDepos.filter(d => {
            if (hideInactiveBranches && !d.hasActivity) return false;
            if (branchSearch) {
                const q = branchSearch.toLowerCase();
                return d.depo_name.toLowerCase().includes(q);
            }
            return true;
        });
    }, [processedDepos, hideInactiveBranches, branchSearch]);

    const activeCount = processedDepos.filter(d => d.hasActivity).length;
    const inactiveCount = processedDepos.length - activeCount;

    // Toggle single branch collapse
    const toggleBranchCollapse = (id) => {
        setCollapsedBranches(prev => ({ ...prev, [id]: !prev[id] }));
    };

    // Collapse or expand all branches
    const toggleCollapseAllBranches = () => {
        const allCollapsed = filteredDepos.every(d => collapsedBranches[d.depo_id]);
        const next = {};
        filteredDepos.forEach(d => { next[d.depo_id] = !allCollapsed; });
        setCollapsedBranches(next);
    };

    // ── Accounts Filtering & Grouping ──
    const typeOrder = ['Asset', 'Liability', 'Equity', 'Revenue', 'Expense'];
    const typeMeta = {
        Asset: { label: 'Assets', labelAr: 'الأصول', normal: 'Debit', icon: '🏦', color: 'emerald' },
        Liability: { label: 'Liabilities', labelAr: 'الالتزامات', normal: 'Credit', icon: '📋', color: 'rose' },
        Equity: { label: 'Equity', labelAr: 'حقوق الملكية', normal: 'Credit', icon: '💎', color: 'purple' },
        Revenue: { label: 'Revenue', labelAr: 'الإيرادات', normal: 'Credit', icon: '💰', color: 'emerald' },
        Expense: { label: 'Expenses', labelAr: 'المصروفات', normal: 'Debit', icon: '💸', color: 'rose' },
    };

    const filteredAccounts = useMemo(() => {
        return accounts.filter(a => {
            const q = searchQ.toLowerCase();
            const matchQ = !q || a.name.toLowerCase().includes(q) || String(a.code).toLowerCase().includes(q);
            const matchT = typeFilter === 'All' || a.type === typeFilter;
            return matchQ && matchT;
        });
    }, [accounts, searchQ, typeFilter]);

    const groupedAccounts = useMemo(() => {
        const res = {};
        typeOrder.forEach(t => { res[t] = []; });
        filteredAccounts.forEach(a => {
            if (!res[a.type]) res[a.type] = [];
            res[a.type].push(a);
        });
        return res;
    }, [filteredAccounts]);

    const toggleCategoryCollapse = (type) => {
        setCollapsedCategories(prev => ({ ...prev, [type]: !prev[type] }));
    };

    const toggleCollapseAllCategories = () => {
        const allCollapsed = typeOrder.every(t => collapsedCategories[t]);
        const next = {};
        typeOrder.forEach(t => { next[t] = !allCollapsed; });
        setCollapsedCategories(next);
    };

    // Grand ledger sums
    const totalDebitSum = accounts.reduce((s, a) => s + parseFloat(a.total_debit || 0), 0);
    const totalCreditSum = accounts.reduce((s, a) => s + parseFloat(a.total_credit || 0), 0);

    // Balance Sheet & P&L Aggregates
    const totalAssetsBalance = (groupedAccounts['Asset'] || []).reduce((s, a) => s + parseFloat(a.current_balance || 0), 0);
    const totalLiabilitiesBalance = (groupedAccounts['Liability'] || []).reduce((s, a) => s + parseFloat(a.current_balance || 0), 0);
    const totalEquityBalance = (groupedAccounts['Equity'] || []).reduce((s, a) => s + parseFloat(a.current_balance || 0), 0);
    const totalRevenueBalance = (groupedAccounts['Revenue'] || []).reduce((s, a) => s + parseFloat(a.current_balance || 0), 0);
    const totalExpenseBalance = (groupedAccounts['Expense'] || []).reduce((s, a) => s + parseFloat(a.current_balance || 0), 0);
    const netProfitBalance = totalRevenueBalance - totalExpenseBalance;
    const cashBankBalance = accounts.find(a => a.code === '1000')?.current_balance || 0;

    // ── Export to Excel ──
    const exportToExcel = () => {
        const wb = XLSX.utils.book_new();

        if (activeTab === 'summary') {
            const summaryRows = [
                ["Metric", "Amount (CFA)"],
                ["Sales Cash Collected", cashIn],
                ["Total Sales Billed", totalSalesBilled],
                ["Stock Purchases (Achats)", totalPurchases],
                ["Operating Expenses", totalExpenses],
                ["Net Operating Cash Flow", netCashFlow],
                ["Stock Inventory (At Cost)", totalInventoryCost],
                ["Stock Valuation (At Retail)", totalInventoryRetail],
                ["Total Consolidated Capital", totalConsolidatedCapital],
                ["", ""],
                ["Branch", "Cash Collected (CFA)", "Purchases (CFA)", "Expenses (CFA)", "Net Cash (CFA)", "Stock Cost (CFA)", "Stock Retail (CFA)"]
            ];

            filteredDepos.forEach(d => {
                summaryRows.push([d.depo_name, d.cIn, d.purch, d.exp, d.net, d.stockCost, d.stockRetail]);
            });

            const ws = XLSX.utils.aoa_to_sheet(summaryRows);
            XLSX.utils.book_append_sheet(wb, ws, "Financial Summary");
            XLSX.writeFile(wb, `Financial_Summary_${startDate}_to_${endDate}.xlsx`);
        } else {
            const accountRows = [
                ["Code", "Account Name", "Type", "Total Debit (CFA)", "Total Credit (CFA)", "Net Balance (CFA)"]
            ];

            filteredAccounts.forEach(a => {
                accountRows.push([a.code, a.name, a.type, parseFloat(a.total_debit || 0), parseFloat(a.total_credit || 0), parseFloat(a.current_balance || 0)]);
            });

            const ws = XLSX.utils.aoa_to_sheet(accountRows);
            XLSX.utils.book_append_sheet(wb, ws, "Chart of Accounts");
            XLSX.writeFile(wb, `Chart_of_Accounts_${startDate}_to_${endDate}.xlsx`);
        }
    };

    // ── Print Functionality ──
    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        const coName = systemSettings?.company_name || 'SUGIMCO';
        const coSubtitle = systemSettings?.company_subtitle || 'SUPER MARCHE Yazbek';

        let content = '';

        if (activeTab === 'summary') {
            content = `
                <div class="header">
                    <h2>${coName}</h2>
                    <p>${coSubtitle}</p>
                    <h3>Financial & Branch Performance Summary</h3>
                    <p style="color: #64748b;">Period: ${startDate} to ${endDate}</p>
                </div>

                <div class="metrics-grid">
                    <div class="box"><div class="l">Cash Collected</div><div class="v">CFA ${fmt(cashIn)}</div></div>
                    <div class="box"><div class="l">Stock Purchases</div><div class="v">CFA ${fmt(totalPurchases)}</div></div>
                    <div class="box"><div class="l">Expenses</div><div class="v">CFA ${fmt(totalExpenses)}</div></div>
                    <div class="box"><div class="l">Net Cash Flow</div><div class="v">${netCashFlow >= 0 ? '+' : ''}${fmt(netCashFlow)}</div></div>
                    <div class="box"><div class="l">Stock (Cost)</div><div class="v">CFA ${fmt(totalInventoryCost)}</div></div>
                    <div class="box"><div class="l">Stock (Retail)</div><div class="v">CFA ${fmt(totalInventoryRetail)}</div></div>
                </div>

                <h4 style="margin: 20px 0 10px; text-transform: uppercase;">Branch Breakdown (${filteredDepos.length} Branches)</h4>
                <table>
                    <thead>
                        <tr>
                            <th>Branch</th>
                            <th class="r">Cash In</th>
                            <th class="r">Purchases</th>
                            <th class="r">Expenses</th>
                            <th class="r">Net Cash</th>
                            <th class="r">Stock (Cost)</th>
                            <th class="r">Stock (Retail)</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${filteredDepos.map(d => `
                            <tr>
                                <td><strong>${d.depo_name}</strong></td>
                                <td class="r">${fmt(d.cIn)}</td>
                                <td class="r">${fmt(d.purch)}</td>
                                <td class="r">${fmt(d.exp)}</td>
                                <td class="r" style="font-weight: bold;">${d.net >= 0 ? '+' : ''}${fmt(d.net)}</td>
                                <td class="r">${fmt(d.stockCost)}</td>
                                <td class="r">${fmt(d.stockRetail)}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                    <tfoot>
                        <tr style="background: #f8fafc; font-weight: bold;">
                            <td>Total</td>
                            <td class="r">${fmt(cashIn)}</td>
                            <td class="r">${fmt(totalPurchases)}</td>
                            <td class="r">${fmt(totalExpenses)}</td>
                            <td class="r">${fmt(netCashFlow)}</td>
                            <td class="r">${fmt(totalInventoryCost)}</td>
                            <td class="r">${fmt(totalInventoryRetail)}</td>
                        </tr>
                    </tfoot>
                </table>
            `;
        } else {
            content = `
                <div class="header">
                    <h2>${coName}</h2>
                    <p>${coSubtitle}</p>
                    <h3>Chart of Accounts (General Ledger)</h3>
                    <p style="color: #64748b;">Period: ${startDate} to ${endDate}</p>
                </div>

                <table>
                    <thead>
                        <tr>
                            <th style="width: 15%;">Code</th>
                            <th style="width: 35%;">Account Name</th>
                            <th style="width: 15%;">Type</th>
                            <th class="r" style="width: 15%;">Debit</th>
                            <th class="r" style="width: 20%;">Credit / Balance</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${typeOrder.filter(t => typeFilter === 'All' || typeFilter === t).map(t => {
                const items = groupedAccounts[t] || [];
                if (items.length === 0) return '';
                const grpBal = items.reduce((s, a) => s + parseFloat(a.current_balance || 0), 0);
                return `
                                <tr style="background: #f1f5f9; font-weight: bold;">
                                    <td colspan="4">${typeMeta[t].icon} ${t.toUpperCase()}</td>
                                    <td class="r">CFA ${fmt(grpBal)}</td>
                                </tr>
                                ${items.map(a => `
                                    <tr>
                                        <td><code>${a.code}</code></td>
                                        <td>${a.name}</td>
                                        <td>${a.type}</td>
                                        <td class="r">${fmt(a.total_debit)}</td>
                                        <td class="r font-bold">${fmt(a.current_balance)}</td>
                                    </tr>
                                `).join('')}
                            `;
            }).join('')}
                    </tbody>
                </table>
            `;
        }

        const html = `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="utf-8">
                <title>${coName} - Financial Report</title>
                <style>
                    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: 30px; font-size: 11px; color: #111; line-height: 1.4; }
                    .header { text-align: center; border-bottom: 2px solid #111; padding-bottom: 12px; margin-bottom: 20px; }
                    .header h2 { margin: 0; font-size: 22px; text-transform: uppercase; }
                    .header p { margin: 3px 0 0; }
                    .metrics-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 20px; }
                    .box { border: 1px solid #e2e8f0; padding: 10px; border-radius: 6px; background: #f8fafc; }
                    .box .l { font-size: 9px; text-transform: uppercase; color: #64748b; font-weight: bold; }
                    .box .v { font-size: 14px; font-weight: bold; margin-top: 3px; }
                    table { width: 100%; border-collapse: collapse; margin-top: 10px; }
                    th, td { border: 1px solid #e2e8f0; padding: 7px 10px; text-align: left; }
                    th { background: #f8fafc; font-weight: bold; text-transform: uppercase; font-size: 10px; }
                    .r { text-align: right; }
                </style>
            </head>
            <body>
                ${content}
            </body>
            </html>
        `;

        printWindow.document.open();
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
            printWindow.print();
            printWindow.close();
        }, 250);
    };

    // ── CRUD Handlers ──
    const handleAdd = async (e) => {
        e.preventDefault();
        const res = await authFetch('/api/accounts', { method: 'POST', body: JSON.stringify(form) });
        if (res.ok) {
            setIsAddModalOpen(false);
            fetchAccounts();
            setForm({ code: '', name: '', type: 'Asset' });
        } else {
            alert(t('Error adding account'));
        }
    };

    const handleEdit = async (e) => {
        e.preventDefault();
        const res = await authFetch('/api/accounts', { method: 'PUT', body: JSON.stringify(editForm) });
        if (res.ok) {
            setIsEditModalOpen(false);
            fetchAccounts();
        } else {
            alert(t('Error updating account'));
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('Delete this account?'))) return;
        const res = await authFetch(`/api/accounts?id=${id}`, { method: 'DELETE' });
        if (res.ok) {
            fetchAccounts();
        } else {
            const err = await res.json();
            alert(err.message || t('Cannot delete'));
        }
    };

    return (
        <div className="space-y-6 pb-16 max-w-[1600px] mx-auto font-sans antialiased text-gray-900">

            {/* ── Header Area (Exactly Matching FinancialSummary.jsx) ── */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2.5">
                        <FileText className="w-6 h-6 text-indigo-600" />
                        {t('Financial Accounts Report')}
                    </h2>
                    <p className="text-gray-500 text-sm mt-1">
                        {t('Bilingual audit and category breakdown of double-entry ledger records & branch cash.')}
                    </p>
                </div>

                {/* Date Controls & Action Buttons */}
                <div className="flex flex-wrap items-center gap-3 bg-gray-50 p-2 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded border border-gray-200">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <input
                            type="date"
                            value={startDate}
                            onChange={e => setStartDate(e.target.value)}
                            className="bg-transparent text-sm font-semibold text-gray-700 outline-none cursor-pointer"
                        />
                    </div>
                    <span className="text-gray-400 text-sm font-bold">→</span>
                    <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded border border-gray-200">
                        <input
                            type="date"
                            value={endDate}
                            onChange={e => setEndDate(e.target.value)}
                            className="bg-transparent text-sm font-semibold text-gray-700 outline-none cursor-pointer"
                        />
                    </div>
                    <button
                        onClick={() => { fetchSummary(); fetchAccounts(); }}
                        className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition flex items-center gap-1.5 shadow-sm cursor-pointer"
                    >
                        <Filter className="w-4 h-4" /> {t('Apply')}
                    </button>
                    <button
                        onClick={exportToExcel}
                        className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700 transition flex items-center gap-1.5 shadow-sm cursor-pointer"
                    >
                        <Download className="w-4 h-4" /> Excel
                    </button>
                    <button
                        onClick={handlePrint}
                        className="bg-slate-800 text-white px-3.5 py-2 rounded-lg text-sm font-medium hover:bg-slate-700 transition flex items-center gap-1.5 shadow-sm cursor-pointer"
                    >
                        <Printer className="w-4 h-4 text-slate-300" /> {t('Print')}
                    </button>
                </div>
            </div>

            {/* ── Tab Switcher Bar ── */}
            <div className="flex items-center justify-between border-b border-gray-200 pb-1">
                <div className="flex gap-2">
                    <button
                        onClick={() => setActiveTab('summary')}
                        className={`px-5 py-2.5 rounded-t-lg text-sm font-bold transition-all flex items-center gap-2 cursor-pointer border-b-2 ${activeTab === 'summary'
                                ? 'border-indigo-600 text-indigo-700 bg-white shadow-xs'
                                : 'border-transparent text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                            }`}
                    >
                        <Building2 className="w-4 h-4" />
                        <span>{t('Summary & Branches')}</span>
                    </button>

                    <button
                        onClick={() => setActiveTab('accounts')}
                        className={`px-5 py-2.5 rounded-t-lg text-sm font-bold transition-all flex items-center gap-2 cursor-pointer border-b-2 ${activeTab === 'accounts'
                                ? 'border-indigo-600 text-indigo-700 bg-white shadow-xs'
                                : 'border-transparent text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                            }`}
                    >
                        <Layers className="w-4 h-4" />
                        <span>{t('Chart of Accounts')}</span>
                        <span className={`px-2 py-0.2 rounded-full text-xs font-semibold ${activeTab === 'accounts' ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-200 text-gray-600'
                            }`}>
                            {accounts.length}
                        </span>
                    </button>
                </div>

                {activeTab === 'accounts' && isAdmin && (
                    <button
                        onClick={() => setIsAddModalOpen(true)}
                        className="bg-indigo-600 text-white px-3.5 py-1.5 rounded-lg text-xs font-semibold hover:bg-indigo-700 transition flex items-center gap-1 shadow-xs cursor-pointer mb-1"
                    >
                        <Plus className="w-3.5 h-3.5" />
                        <span>{t('Add Account')}</span>
                    </button>
                )}
            </div>

            {/* ── Executive Overview Section (Top Metric Cards - Exact Matching Style) ── */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {activeTab === 'summary' ? (
                    <>
                        {/* Card 1: Total Cash on Hand */}
                        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                            <div className="flex items-center justify-between mb-3">
                                <span className="p-2.5 bg-indigo-50 text-indigo-600 rounded-lg">
                                    <DollarSign className="w-5 h-5" />
                                </span>
                                <span className="text-xs font-semibold text-indigo-700 bg-indigo-50 px-2.5 py-1 rounded">
                                    {t('Real-time Liquidity')}
                                </span>
                            </div>
                            <p className="text-gray-500 font-medium text-xs uppercase tracking-wider mb-1">
                                {t('Total Cash Collected')} | إجمالي النقد
                            </p>
                            <h3 className="text-3xl font-bold text-gray-900 tracking-tight">
                                {fmt(cashIn)}
                                <span className="text-base font-normal ml-2 text-gray-500">CFA</span>
                            </h3>
                            <p className="text-xs text-gray-400 font-medium mt-1">
                                {t('Total Billed')}: CFA {fmt(totalSalesBilled)}
                            </p>
                        </div>

                        {/* Card 2: Total Consolidated Capital */}
                        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                            <div className="flex items-center justify-between mb-3">
                                <span className="p-2.5 bg-emerald-50 text-emerald-600 rounded-lg">
                                    <TrendingDown className="w-5 h-5 rotate-180" />
                                </span>
                                <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded">
                                    {t('Stakeholder Equity')}
                                </span>
                            </div>
                            <p className="text-gray-500 font-medium text-xs uppercase tracking-wider mb-1">
                                {t('Consolidated Capital')} | رأس المال
                            </p>
                            <h3 className="text-3xl font-bold text-gray-900 tracking-tight">
                                {fmt(totalConsolidatedCapital)}
                                <span className="text-base font-normal ml-2 text-gray-500">CFA</span>
                            </h3>
                            <p className="text-xs text-emerald-600 font-medium mt-1">
                                {t('Liquid Cash Position + Stock Valuation')}
                            </p>
                        </div>

                        {/* Card 3: Stock Inventory Valuation */}
                        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                            <div className="flex items-center justify-between mb-3">
                                <span className="p-2.5 bg-amber-50 text-amber-600 rounded-lg">
                                    <Layers className="w-5 h-5" />
                                </span>
                                <span className="text-xs font-semibold text-amber-700 bg-amber-50 px-2.5 py-1 rounded">
                                    {t('Inventory Asset')}
                                </span>
                            </div>
                            <p className="text-gray-500 font-medium text-xs uppercase tracking-wider mb-1">
                                {t('Stock Inventory Value')} | المخزون
                            </p>
                            <h3 className="text-3xl font-bold text-gray-900 tracking-tight">
                                {fmt(totalInventoryRetail)}
                                <span className="text-base font-normal ml-2 text-gray-500">CFA</span>
                            </h3>
                            <p className="text-xs text-gray-400 font-medium mt-1">
                                {t('Cost')}: CFA {fmt(totalInventoryCost)} · {t('Gain')}: +{fmt(unrealizedInventoryMargin)}
                            </p>
                        </div>

                        {/* Card 4: Net Operating Cash Flow */}
                        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                            <div className="flex items-center justify-between mb-3">
                                <span className={`p-2.5 rounded-lg ${netCashFlow >= 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                                    {netCashFlow >= 0 ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                                </span>
                                <span className={`text-xs font-semibold px-2.5 py-1 rounded ${netCashFlow >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                                    {netCashFlow >= 0 ? t('Cash Surplus') : t('Net Cash Outflow')}
                                </span>
                            </div>
                            <p className="text-gray-500 font-medium text-xs uppercase tracking-wider mb-1">
                                {t('Net Cash Flow')} | صافي النقد
                            </p>
                            <h3 className={`text-3xl font-bold tracking-tight ${netCashFlow >= 0 ? 'text-emerald-700' : 'text-gray-900'}`}>
                                {netCashFlow >= 0 ? '+' : ''}{fmt(netCashFlow)}
                                <span className="text-base font-normal ml-2 text-gray-500">CFA</span>
                            </h3>
                            <p className="text-xs text-gray-400 font-medium mt-1">
                                {t('Cash In')} − {t('Purchases')} ({fmt(totalPurchases)}) − {t('Expenses')} ({fmt(totalExpenses)})
                            </p>
                        </div>
                    </>
                ) : (
                    <>
                        {/* Card 1: Total Assets */}
                        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                            <div className="flex items-center justify-between mb-3">
                                <span className="p-2.5 bg-indigo-50 text-indigo-600 rounded-lg">
                                    <Building2 className="w-5 h-5" />
                                </span>
                                <span className="text-xs font-semibold text-indigo-700 bg-indigo-50 px-2.5 py-1 rounded">
                                    {t('Balance Sheet')}
                                </span>
                            </div>
                            <p className="text-gray-500 font-medium text-xs uppercase tracking-wider mb-1">
                                {t('Total Assets')} | إجمالي الأصول
                            </p>
                            <h3 className="text-3xl font-bold text-gray-900 tracking-tight">
                                {fmt(totalAssetsBalance)}
                                <span className="text-base font-normal ml-2 text-gray-500">CFA</span>
                            </h3>
                            <p className="text-xs text-gray-400 font-medium mt-1">
                                {t('Cash')} + {t('Stock Inventory')} + {t('Receivables')}
                            </p>
                        </div>

                        {/* Card 2: Cash & Bank Liquidity */}
                        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                            <div className="flex items-center justify-between mb-3">
                                <span className="p-2.5 bg-emerald-50 text-emerald-600 rounded-lg">
                                    <DollarSign className="w-5 h-5" />
                                </span>
                                <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded">
                                    {t('Liquid Funds')}
                                </span>
                            </div>
                            <p className="text-gray-500 font-medium text-xs uppercase tracking-wider mb-1">
                                {t('Cash / Bank (1000)')} | السيولة النقدية
                            </p>
                            <h3 className="text-3xl font-bold text-emerald-700 tracking-tight">
                                {fmt(cashBankBalance)}
                                <span className="text-base font-normal ml-2 text-gray-500">CFA</span>
                            </h3>
                            <p className="text-xs text-emerald-600 font-medium mt-1">
                                {t('Physical registers & active bank accounts')}
                            </p>
                        </div>

                        {/* Card 3: Total Liabilities */}
                        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                            <div className="flex items-center justify-between mb-3">
                                <span className="p-2.5 bg-rose-50 text-rose-600 rounded-lg">
                                    <AlertCircle className="w-5 h-5" />
                                </span>
                                <span className="text-xs font-semibold text-rose-700 bg-rose-50 px-2.5 py-1 rounded">
                                    {t('Obligations')}
                                </span>
                            </div>
                            <p className="text-gray-500 font-medium text-xs uppercase tracking-wider mb-1">
                                {t('Total Liabilities')} | إجمالي الالتزامات
                            </p>
                            <h3 className="text-3xl font-bold text-gray-900 tracking-tight">
                                {fmt(totalLiabilitiesBalance)}
                                <span className="text-base font-normal ml-2 text-gray-500">CFA</span>
                            </h3>
                            <p className="text-xs text-gray-400 font-medium mt-1">
                                {t('Tax Payable (2100)')} + {t('Accounts Payable (2000)')}
                            </p>
                        </div>

                        {/* Card 4: Net Operating Profit / Earnings */}
                        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                            <div className="flex items-center justify-between mb-3">
                                <span className={`p-2.5 rounded-lg ${netProfitBalance >= 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                                    {netProfitBalance >= 0 ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                                </span>
                                <span className={`text-xs font-semibold px-2.5 py-1 rounded ${netProfitBalance >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                                    {netProfitBalance >= 0 ? t('Operating Profit') : t('Net Loss')}
                                </span>
                            </div>
                            <p className="text-gray-500 font-medium text-xs uppercase tracking-wider mb-1">
                                {t('Net Profit (Earnings)')} | صافي الأرباح
                            </p>
                            <h3 className={`text-3xl font-bold tracking-tight ${netProfitBalance >= 0 ? 'text-emerald-700' : 'text-gray-900'}`}>
                                {netProfitBalance >= 0 ? '+' : ''}{fmt(netProfitBalance)}
                                <span className="text-base font-normal ml-2 text-gray-500">CFA</span>
                            </h3>
                            <p className="text-xs text-gray-400 font-medium mt-1">
                                {t('Revenue')} ({fmt(totalRevenueBalance)}) − {t('COGS & Expenses')} ({fmt(totalExpenseBalance)})
                            </p>
                        </div>
                    </>
                )}
            </div>

            {/* ── Critical Audit Notice (Notice Box matching screenshot) ── */}
            {activeTab === 'summary' && netCashFlow < 0 && (
                <div className="bg-white rounded-xl border border-rose-200 overflow-hidden shadow-sm">
                    <div className="bg-rose-50 border-b border-rose-200 px-6 py-4 flex items-center justify-between">
                        <h3 className="text-rose-900 text-base font-bold flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 text-rose-600" />
                            {t('Audit Notices & Financial Notes')}
                        </h3>
                        <span className="bg-rose-100 text-rose-800 px-2.5 py-0.5 rounded text-xs font-semibold">
                            1 {t('Notice')}
                        </span>
                    </div>
                    <div className="p-6 space-y-3">
                        <div className="bg-rose-50/50 border border-rose-200/60 p-4 rounded-lg flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="space-y-0.5 flex-1">
                                <p className="text-sm font-semibold text-rose-900">
                                    {t('Stock Inventory Outflow: Purchases of CFA')} {fmt(totalPurchases)} {t('exceeded period cash collections of CFA')} {fmt(cashIn)}.
                                </p>
                                <p className="text-xs text-rose-700">
                                    {t('Cash is currently invested into warehouse inventory. Stock worth CFA')} {fmt(totalInventoryRetail)} {t('is held across branches.')}
                                </p>
                            </div>
                            <span className="bg-rose-600 text-white px-2.5 py-1 rounded text-xs font-medium self-start md:self-center">
                                {t('Stock Investment')}
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* In Chart of Accounts Tab: Double-Entry Balance Verification Banner */}
            {activeTab === 'accounts' && (
                <div className="bg-white rounded-xl border border-emerald-200 overflow-hidden shadow-sm">
                    <div className="bg-emerald-50 border-b border-emerald-200 px-6 py-3 flex items-center justify-between">
                        <h3 className="text-emerald-900 text-sm font-bold flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                            {t('General Ledger Verification & Double-Entry Health')}
                        </h3>
                        <span className="bg-emerald-100 text-emerald-800 px-2.5 py-0.5 rounded text-xs font-semibold">
                            {t('Balanced')}
                        </span>
                    </div>
                    <div className="p-4 px-6 flex flex-col md:flex-row md:items-center justify-between gap-2 text-xs">
                        <div className="space-y-1">
                            <p className="text-gray-800 font-medium">
                                <strong className="text-emerald-700">{t('Balance Sheet Equation Verified')}</strong>:
                                {' '}{t('Assets')} ({fmt(totalAssetsBalance)} CFA) = {t('Liabilities')} ({fmt(totalLiabilitiesBalance)} CFA) + {t('Equity & Retained Earnings')} ({fmt(totalEquityBalance + netProfitBalance)} CFA).
                            </p>
                            <p className="text-gray-500">
                                {t('All posted sales, inventory cost of goods sold, and purchases are balanced with 0 discrepancy.')}
                            </p>
                        </div>
                        <div className="flex items-center gap-2 text-gray-500">
                            <span>{t('Total Ledger Debits')}: <strong className="text-gray-800">{fmt(totalDebitSum)} CFA</strong></span>
                            <span>•</span>
                            <span>{t('Total Credits')}: <strong className="text-gray-800">{fmt(totalCreditSum)} CFA</strong></span>
                        </div>
                    </div>
                </div>
            )}

            {/* ════════════════════════════════════════════════════════════════
                TAB 1: CATEGORIZED SPENDING & EARNINGS BY BRANCH
            ════════════════════════════════════════════════════════════════ */}
            {activeTab === 'summary' && (
                <div className="space-y-4">

                    {/* Section Header with Minimizing & Filtering Controls */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-1 pt-2">
                        <div className="flex items-center gap-2">
                            <Building2 className="w-5 h-5 text-gray-600" />
                            <h3 className="text-lg font-bold text-gray-900">{t('Categorized Branch Spending & Earnings')}</h3>
                            <span className="text-xs text-gray-400">({filteredDepos.length} {t('branches')})</span>
                        </div>

                        <div className="flex flex-wrap items-center gap-2.5">
                            {/* Branch Search */}
                            <div className="relative">
                                <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-2.5" />
                                <input
                                    type="text"
                                    placeholder={t('Search branch...')}
                                    value={branchSearch}
                                    onChange={e => setBranchSearch(e.target.value)}
                                    className="pl-8 pr-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-500 w-48 shadow-xs"
                                />
                            </div>

                            {/* Hide Inactive Toggle (Solves the issue of empty branches cluttering!) */}
                            <label className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-50 transition shadow-xs select-none">
                                <input
                                    type="checkbox"
                                    checked={hideInactiveBranches}
                                    onChange={e => setHideInactiveBranches(e.target.checked)}
                                    className="rounded text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                />
                                <span>{t('Hide Empty (0 Activity)')}</span>
                                {inactiveCount > 0 && hideInactiveBranches && (
                                    <span className="text-[10px] text-gray-400">({inactiveCount})</span>
                                )}
                            </label>

                            {/* View Mode Toggle: Cards vs Table */}
                            <div className="flex items-center bg-gray-100 p-0.5 rounded-lg border border-gray-200">
                                <button
                                    onClick={() => setBranchViewMode('cards')}
                                    className={`px-2 py-1 rounded text-xs font-semibold transition cursor-pointer ${branchViewMode === 'cards' ? 'bg-white text-gray-900 shadow-xs' : 'text-gray-500 hover:text-gray-900'
                                        }`}
                                >
                                    {t('Cards')}
                                </button>
                                <button
                                    onClick={() => setBranchViewMode('table')}
                                    className={`px-2 py-1 rounded text-xs font-semibold transition cursor-pointer ${branchViewMode === 'table' ? 'bg-white text-gray-900 shadow-xs' : 'text-gray-500 hover:text-gray-900'
                                        }`}
                                >
                                    {t('Table')}
                                </button>
                            </div>

                            {/* Collapse/Expand All Button */}
                            {branchViewMode === 'cards' && filteredDepos.length > 0 && (
                                <button
                                    onClick={toggleCollapseAllBranches}
                                    className="px-3 py-1.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-xs font-semibold rounded-lg transition shadow-xs cursor-pointer"
                                >
                                    {filteredDepos.every(d => collapsedBranches[d.depo_id]) ? t('Expand All') : t('Collapse All')}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* ── Cards Presentation (Exactly Matching Screenshot's Category Cards) ── */}
                    {branchViewMode === 'cards' && (
                        <div className="space-y-4">
                            {filteredDepos.length === 0 ? (
                                <div className="bg-white rounded-xl p-12 text-center text-gray-400 border border-gray-200">
                                    <p className="text-base font-medium">{t('No branches found matching current filters.')}</p>
                                </div>
                            ) : (
                                filteredDepos.map(depo => {
                                    const isCollapsed = collapsedBranches[depo.depo_id];
                                    const isPositive = depo.net >= 0;

                                    return (
                                        <div key={depo.depo_id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                                            {/* Card Header (Clickable to Collapse / Minimize) */}
                                            <div
                                                onClick={() => toggleBranchCollapse(depo.depo_id)}
                                                className="p-4 bg-gray-50/80 border-b border-gray-200 flex justify-between items-center cursor-pointer hover:bg-gray-100/70 transition select-none"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-2.5 h-6 rounded-full ${isPositive ? 'bg-emerald-500' : 'bg-rose-500'}`}></div>
                                                    <div>
                                                        <h4 className="text-base font-bold text-gray-900 flex items-center gap-2">
                                                            <span>{depo.depo_name}</span>
                                                            {!depo.hasActivity && (
                                                                <span className="text-xs font-normal text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                                                                    {t('Inactive')}
                                                                </span>
                                                            )}
                                                        </h4>
                                                        <p className="text-xs text-gray-500 font-medium">
                                                            {depo.invCount} {t('invoices')} • {depo.poCount} {t('purchase orders')} • {depo.expCount} {t('expenses')}
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-4 text-right">
                                                    <div>
                                                        <p className="text-xs text-gray-500 mb-0.5">{t('Subtotal (Net Cash)')} | صافي النقد</p>
                                                        <p className={`text-lg font-bold ${isPositive ? 'text-emerald-700' : 'text-rose-700'}`}>
                                                            {isPositive ? '+' : ''}{fmt(depo.net)} <span className="text-xs font-normal text-gray-500">CFA</span>
                                                        </p>
                                                    </div>
                                                    <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isCollapsed ? '' : 'rotate-180'}`} />
                                                </div>
                                            </div>

                                            {/* Table Body (Collapsible) */}
                                            {!isCollapsed && (
                                                <div className="overflow-x-auto">
                                                    <table className="w-full text-left text-sm">
                                                        <thead>
                                                            <tr className="bg-white text-gray-500 text-xs border-b border-gray-100">
                                                                <th className="py-3 px-4 w-40">{t('Account / Flow')}</th>
                                                                <th className="py-3 px-4">{t('Description')} / البيان</th>
                                                                <th className="py-3 px-4 text-right w-48">{t('Inflow (Cash)')} / مدين</th>
                                                                <th className="py-3 px-4 text-right w-48">{t('Outflow (Paid)')} / دائن</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-gray-100">
                                                            {/* Sales Inflow Row */}
                                                            <tr className="hover:bg-gray-50/60 transition-colors">
                                                                <td className="py-2.5 px-4 text-xs font-semibold text-indigo-700 flex items-center gap-1.5">
                                                                    <span>💵</span>
                                                                    <span>{t('Cash / Sales')}</span>
                                                                </td>
                                                                <td className="py-2.5 px-4 text-sm text-gray-800">
                                                                    {t('Sales Revenue Collected')} ({depo.invCount} {t('invoices')}, {t('Total billed')}: {fmt(depo.total_sales)} CFA)
                                                                </td>
                                                                <td className="py-2.5 px-4 text-right font-medium text-emerald-600">
                                                                    {fmt(depo.cIn)}
                                                                </td>
                                                                <td className="py-2.5 px-4 text-right font-medium text-gray-400">—</td>
                                                            </tr>

                                                            {/* Purchases Outflow Row */}
                                                            <tr className="hover:bg-gray-50/60 transition-colors">
                                                                <td className="py-2.5 px-4 text-xs font-semibold text-amber-700 flex items-center gap-1.5">
                                                                    <span>📦</span>
                                                                    <span>{t('Achats (Stock)')}</span>
                                                                </td>
                                                                <td className="py-2.5 px-4 text-sm text-gray-800">
                                                                    {t('Stock Inventory Purchases')} ({depo.poCount} {t('purchase orders')})
                                                                </td>
                                                                <td className="py-2.5 px-4 text-right font-medium text-gray-400">—</td>
                                                                <td className="py-2.5 px-4 text-right font-medium text-rose-600">
                                                                    {fmt(depo.purch)}
                                                                </td>
                                                            </tr>

                                                            {/* Expenses Row */}
                                                            <tr className="hover:bg-gray-50/60 transition-colors">
                                                                <td className="py-2.5 px-4 text-xs font-semibold text-rose-700 flex items-center gap-1.5">
                                                                    <span>💸</span>
                                                                    <span>{t('Dépenses')}</span>
                                                                </td>
                                                                <td className="py-2.5 px-4 text-sm text-gray-800">
                                                                    {t('Branch Operational Expenses')} ({depo.expCount} {t('entries')})
                                                                </td>
                                                                <td className="py-2.5 px-4 text-right font-medium text-gray-400">—</td>
                                                                <td className="py-2.5 px-4 text-right font-medium text-rose-600">
                                                                    {fmt(depo.exp)}
                                                                </td>
                                                            </tr>

                                                            {/* Stock Inventory Information Row */}
                                                            <tr className="hover:bg-gray-50/60 transition-colors bg-gray-50/30">
                                                                <td className="py-2.5 px-4 text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                                                                    <span>🏢</span>
                                                                    <span>{t('Warehouse Stock')}</span>
                                                                </td>
                                                                <td className="py-2.5 px-4 text-xs text-gray-600">
                                                                    {t('Current Physical Stock Value')}: {t('Cost')} CFA {fmt(depo.stockCost)} • {t('Retail')} CFA {fmt(depo.stockRetail)} ({depo.total_units} {t('units')})
                                                                </td>
                                                                <td className="py-2.5 px-4 text-right text-xs font-semibold text-gray-700">
                                                                    CFA {fmt(depo.stockCost)}
                                                                </td>
                                                                <td className="py-2.5 px-4 text-right text-xs font-bold text-indigo-700">
                                                                    CFA {fmt(depo.stockRetail)}
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                        <tfoot>
                                                            <tr className="bg-gray-50 text-gray-800 font-semibold text-xs border-t border-gray-200">
                                                                <td colSpan="2" className="py-3 px-4 uppercase tracking-wider">{t('Category Subtotals')}</td>
                                                                <td className="py-3 px-4 text-right text-sm text-emerald-600">
                                                                    {fmt(depo.cIn)}
                                                                </td>
                                                                <td className="py-3 px-4 text-right text-sm text-rose-600">
                                                                    {fmt(depo.purch + depo.exp)}
                                                                </td>
                                                            </tr>
                                                        </tfoot>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    )}

                    {/* ── Table View Mode (High Density) ── */}
                    {branchViewMode === 'table' && (
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-sm">
                                    <thead>
                                        <tr className="bg-gray-100 text-gray-700 text-xs font-semibold border-b border-gray-200">
                                            <th className="py-3 px-4">{t('Branch')} / الفرع</th>
                                            <th className="py-3 px-4 text-right text-emerald-700">{t('Cash In')} / تحصيل</th>
                                            <th className="py-3 px-4 text-right text-rose-700">{t('Purchases')} / مشتريات</th>
                                            <th className="py-3 px-4 text-right text-rose-700">{t('Expenses')} / مصاريف</th>
                                            <th className="py-3 px-4 text-right font-bold">{t('Net Cash Flow')} / صافي النقد</th>
                                            <th className="py-3 px-4 text-right text-gray-600">{t('Stock Cost')}</th>
                                            <th className="py-3 px-4 text-right text-indigo-700">{t('Stock Retail')}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {filteredDepos.map((d, idx) => (
                                            <tr key={idx} className="hover:bg-gray-50/60 transition-colors">
                                                <td className="py-2.5 px-4 font-semibold text-gray-900">
                                                    {d.depo_name}
                                                    <span className="text-xs text-gray-400 font-normal ml-2">
                                                        ({d.invCount} inv • {d.poCount} POs)
                                                    </span>
                                                </td>
                                                <td className="py-2.5 px-4 text-right font-medium text-emerald-600">{fmt(d.cIn)}</td>
                                                <td className="py-2.5 px-4 text-right font-medium text-rose-600">{fmt(d.purch)}</td>
                                                <td className="py-2.5 px-4 text-right font-medium text-rose-600">{fmt(d.exp)}</td>
                                                <td className={`py-2.5 px-4 text-right font-bold ${d.net >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                                                    {d.net >= 0 ? '+' : ''}{fmt(d.net)} CFA
                                                </td>
                                                <td className="py-2.5 px-4 text-right text-gray-600">{fmt(d.stockCost)}</td>
                                                <td className="py-2.5 px-4 text-right font-semibold text-indigo-700">{fmt(d.stockRetail)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className="bg-gray-50 text-gray-800 font-bold text-xs border-t-2 border-gray-200">
                                            <td className="py-3 px-4 uppercase tracking-wider">{t('Grand Total Sums')} ({filteredDepos.length} {t('branches')})</td>
                                            <td className="py-3 px-4 text-right text-emerald-600 text-sm">{fmt(cashIn)}</td>
                                            <td className="py-3 px-4 text-right text-rose-600 text-sm">{fmt(totalPurchases)}</td>
                                            <td className="py-3 px-4 text-right text-rose-600 text-sm">{fmt(totalExpenses)}</td>
                                            <td className={`py-3 px-4 text-right text-sm font-black ${netCashFlow >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                                                {netCashFlow >= 0 ? '+' : ''}{fmt(netCashFlow)} CFA
                                            </td>
                                            <td className="py-3 px-4 text-right text-gray-700 text-sm">{fmt(totalInventoryCost)}</td>
                                            <td className="py-3 px-4 text-right text-indigo-700 text-sm">{fmt(totalInventoryRetail)}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ════════════════════════════════════════════════════════════════
                TAB 2: CHART OF ACCOUNTS (CATEGORIZED GENERAL LEDGER)
            ════════════════════════════════════════════════════════════════ */}
            {activeTab === 'accounts' && (
                <div className="space-y-4">

                    {/* Filter & Search Bar */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-1 pt-2">
                        <div className="flex items-center gap-2">
                            <Layers className="w-5 h-5 text-gray-600" />
                            <h3 className="text-lg font-bold text-gray-900">{t('Categorized Spending & Earnings (General Ledger)')}</h3>
                        </div>

                        <div className="flex flex-wrap items-center gap-2.5">
                            {/* Search Input */}
                            <div className="relative">
                                <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-2.5" />
                                <input
                                    type="text"
                                    value={searchQ}
                                    onChange={e => setSearchQ(e.target.value)}
                                    placeholder={t('Search code or name...')}
                                    className="pl-8 pr-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-500 w-48 shadow-xs"
                                />
                            </div>

                            {/* Category Filter Pills */}
                            <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg border border-gray-200 text-xs font-semibold">
                                <button
                                    onClick={() => setTypeFilter('All')}
                                    className={`px-2.5 py-1 rounded transition cursor-pointer ${typeFilter === 'All' ? 'bg-white text-gray-900 shadow-xs' : 'text-gray-500 hover:text-gray-900'
                                        }`}
                                >
                                    {t('All')}
                                </button>
                                {typeOrder.map(type => (
                                    <button
                                        key={type}
                                        onClick={() => setTypeFilter(type)}
                                        className={`px-2 py-1 rounded transition cursor-pointer hidden md:block ${typeFilter === type ? 'bg-white text-gray-900 shadow-xs' : 'text-gray-500 hover:text-gray-900'
                                            }`}
                                    >
                                        {typeMeta[type].icon} {typeMeta[type].label}
                                    </button>
                                ))}
                            </div>

                            {/* Collapse / Expand All */}
                            <button
                                onClick={toggleCollapseAllCategories}
                                className="px-3 py-1.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-xs font-semibold rounded-lg transition shadow-xs cursor-pointer"
                            >
                                {typeOrder.every(t => collapsedCategories[t]) ? t('Expand All') : t('Collapse All')}
                            </button>
                        </div>
                    </div>

                    {/* Category Cards (Exact matching layout from screenshot) */}
                    <div className="space-y-4">
                        {typeOrder.filter(type => typeFilter === 'All' || typeFilter === type).map(type => {
                            const items = groupedAccounts[type] || [];
                            if (items.length === 0 && searchQ) return null;

                            const meta = typeMeta[type];
                            const isCollapsed = collapsedCategories[type];
                            const groupBal = items.reduce((s, a) => s + parseFloat(a.current_balance || 0), 0);
                            const groupDebit = items.reduce((s, a) => s + parseFloat(a.total_debit || 0), 0);
                            const groupCredit = items.reduce((s, a) => s + parseFloat(a.total_credit || 0), 0);

                            return (
                                <div key={type} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                                    {/* Header */}
                                    <div
                                        onClick={() => toggleCategoryCollapse(type)}
                                        className="p-4 bg-gray-50/80 border-b border-gray-200 flex justify-between items-center cursor-pointer hover:bg-gray-100/70 transition select-none"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={`w-2.5 h-6 rounded-full ${meta.color === 'emerald' ? 'bg-emerald-500' : meta.color === 'rose' ? 'bg-rose-500' : 'bg-indigo-500'}`}></div>
                                            <div>
                                                <h4 className="text-base font-bold text-gray-900 flex items-center gap-2">
                                                    <span>{meta.icon}</span>
                                                    <span>{meta.label}</span>
                                                    <span className="text-xs text-gray-400 font-normal">({items.length} {t('accounts')})</span>
                                                </h4>
                                                <p className="text-xs text-gray-500 font-medium">
                                                    {t('Normal Balance')}: <strong className="text-gray-700">{meta.normal}</strong> | {meta.labelAr}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-4 text-right">
                                            <div>
                                                <p className="text-xs text-gray-500 mb-0.5">{t('Subtotal')} | الإجمالي</p>
                                                <p className="text-lg font-bold text-indigo-700">
                                                    {fmt(groupBal)} <span className="text-xs font-normal text-gray-500">CFA</span>
                                                </p>
                                            </div>
                                            <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isCollapsed ? '' : 'rotate-180'}`} />
                                        </div>
                                    </div>

                                    {/* Table */}
                                    {!isCollapsed && (
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-left text-sm">
                                                <thead>
                                                    <tr className="bg-white text-gray-500 text-xs border-b border-gray-100">
                                                        <th className="py-3 px-4 w-28">{t('Code')} / الرمز</th>
                                                        <th className="py-3 px-4">{t('Account Name')} / اسم الحساب</th>
                                                        <th className="py-3 px-4 w-32">{t('Type')} / النوع</th>
                                                        <th className="py-3 px-4 text-right w-44">{t('Debit')} / مدين</th>
                                                        <th className="py-3 px-4 text-right w-44">{t('Credit')} / دائن</th>
                                                        <th className="py-3 px-4 text-right w-48">{t('Balance')} / الرصيد</th>
                                                        {isAdmin && <th className="py-3 px-4 text-center w-24">{t('Actions')}</th>}
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100">
                                                    {items.length === 0 ? (
                                                        <tr>
                                                            <td colSpan={isAdmin ? 7 : 6} className="py-6 px-4 text-center text-gray-400 text-xs">
                                                                {t('No accounts in this category.')}
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        items.map(item => (
                                                            <tr key={item.id} className="hover:bg-gray-50/60 transition-colors">
                                                                <td className="py-2.5 px-4 text-xs font-mono font-bold text-gray-600">{item.code}</td>
                                                                <td className="py-2.5 px-4 text-sm font-semibold text-gray-800">{item.name}</td>
                                                                <td className="py-2.5 px-4 text-xs">
                                                                    <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-gray-100 text-gray-700 border border-gray-200">
                                                                        {item.type}
                                                                    </span>
                                                                </td>
                                                                <td className="py-2.5 px-4 text-right font-medium text-gray-700">
                                                                    {parseFloat(item.total_debit) > 0 ? fmt(item.total_debit) : '—'}
                                                                </td>
                                                                <td className="py-2.5 px-4 text-right font-medium text-gray-700">
                                                                    {parseFloat(item.total_credit) > 0 ? fmt(item.total_credit) : '—'}
                                                                </td>
                                                                <td className={`py-2.5 px-4 text-right font-bold text-sm ${parseFloat(item.current_balance) < 0 ? 'text-rose-600' : 'text-gray-900'}`}>
                                                                    {fmt(item.current_balance)} CFA
                                                                </td>
                                                                {isAdmin && (
                                                                    <td className="py-2.5 px-4 text-center">
                                                                        <div className="flex items-center justify-center gap-1.5 text-xs">
                                                                            <button
                                                                                onClick={() => { setEditForm(item); setIsEditModalOpen(true); }}
                                                                                className="text-indigo-600 hover:text-indigo-800 font-semibold cursor-pointer"
                                                                            >
                                                                                {t('Edit')}
                                                                            </button>
                                                                            <span className="text-gray-300">•</span>
                                                                            <button
                                                                                onClick={() => handleDelete(item.id)}
                                                                                className="text-rose-600 hover:text-rose-800 font-semibold cursor-pointer"
                                                                            >
                                                                                {t('Delete')}
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                )}
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                                <tfoot>
                                                    <tr className="bg-gray-50 text-gray-800 font-semibold text-xs border-t border-gray-200">
                                                        <td colSpan="3" className="py-3 px-4 uppercase tracking-wider">{t('Category Subtotals')}</td>
                                                        <td className="py-3 px-4 text-right text-sm text-gray-800">
                                                            {fmt(groupDebit)}
                                                        </td>
                                                        <td className="py-3 px-4 text-right text-sm text-gray-800">
                                                            {fmt(groupCredit)}
                                                        </td>
                                                        <td className="py-3 px-4 text-right text-sm font-bold text-indigo-700">
                                                            CFA {fmt(groupBal)}
                                                        </td>
                                                        {isAdmin && <td></td>}
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* GRAND TOTALS FOOTER (Exact Matching FinancialSummary.jsx) */}
                    <div className="bg-gray-900 text-white p-6 rounded-xl border border-gray-800 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mt-6">
                        <div>
                            <h4 className="text-xl font-bold uppercase tracking-wider">{t('Grand Total Sums')}</h4>
                            <p className="text-gray-400 text-xs mt-1">{t('Accounting balance verification (Debit = Credit)')}</p>
                        </div>
                        <div className="flex flex-wrap gap-4">
                            <div className="bg-gray-800 border border-gray-700 p-4 rounded-lg min-w-[200px]">
                                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-1">{t('Total Debit')} | مدين</p>
                                <p className="text-2xl font-bold text-rose-400">
                                    {fmt(totalDebitSum)}
                                </p>
                            </div>
                            <div className="bg-gray-800 border border-gray-700 p-4 rounded-lg min-w-[200px]">
                                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-1">{t('Total Credit')} | دائن</p>
                                <p className="text-2xl font-bold text-emerald-400">
                                    {fmt(totalCreditSum)}
                                </p>
                            </div>
                        </div>
                    </div>

                </div>
            )}

            {/* ── Add Account Modal ── */}
            <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title={t('Add Account')}>
                <form onSubmit={handleAdd} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-bold text-gray-700">{t('Account Code')}</label>
                            <input
                                required
                                type="text"
                                placeholder="e.g. 1000"
                                className="w-full border p-2.5 rounded-xl mt-1 font-mono focus:ring-2 focus:ring-indigo-300 outline-none text-sm"
                                value={form.code}
                                onChange={e => setForm({ ...form, code: e.target.value })}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700">{t('Type')}</label>
                            <select
                                className="w-full border p-2.5 rounded-xl mt-1 focus:ring-2 focus:ring-indigo-300 outline-none text-sm cursor-pointer"
                                value={form.type}
                                onChange={e => setForm({ ...form, type: e.target.value })}
                            >
                                {typeOrder.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-gray-700">{t('Account Name')}</label>
                        <input
                            required
                            placeholder="e.g. Cash / Bank"
                            className="w-full border p-2.5 rounded-xl mt-1 focus:ring-2 focus:ring-indigo-300 outline-none text-sm"
                            value={form.name}
                            onChange={e => setForm({ ...form, name: e.target.value })}
                        />
                    </div>
                    <button
                        type="submit"
                        className="w-full bg-indigo-600 text-white py-3 rounded-xl font-bold text-sm uppercase tracking-wider hover:bg-indigo-700 transition-all shadow-md mt-2 cursor-pointer"
                    >
                        {t('Save Account')}
                    </button>
                </form>
            </Modal>

            {/* ── Edit Account Modal ── */}
            <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title={t('Edit Account')}>
                <form onSubmit={handleEdit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-bold text-gray-700">{t('Account Code')}</label>
                            <input
                                required
                                type="text"
                                className="w-full border p-2.5 rounded-xl mt-1 font-mono focus:ring-2 focus:ring-indigo-300 outline-none text-sm"
                                value={editForm.code}
                                onChange={e => setEditForm({ ...editForm, code: e.target.value })}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700">{t('Type')}</label>
                            <select
                                className="w-full border p-2.5 rounded-xl mt-1 focus:ring-2 focus:ring-indigo-300 outline-none text-sm cursor-pointer"
                                value={editForm.type}
                                onChange={e => setEditForm({ ...editForm, type: e.target.value })}
                            >
                                {typeOrder.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-gray-700">{t('Account Name')}</label>
                        <input
                            required
                            className="w-full border p-2.5 rounded-xl mt-1 focus:ring-2 focus:ring-indigo-300 outline-none text-sm"
                            value={editForm.name}
                            onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                        />
                    </div>
                    <button
                        type="submit"
                        className="w-full bg-indigo-600 text-white py-3 rounded-xl font-bold text-sm uppercase tracking-wider hover:bg-indigo-700 transition-all shadow-md mt-2 cursor-pointer"
                    >
                        {t('Update Account')}
                    </button>
                </form>
            </Modal>

        </div>
    );
}
